import React, { useEffect, useMemo } from 'react';
import {
  Alert,
  Avatar,
  Button,
  Card,
  Descriptions,
  Divider,
  Grid,
  Image,
  Skeleton,
  Space,
  Statistic,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  EnvironmentOutlined,
  ExportOutlined,
  GlobalOutlined,
  MailOutlined,
  PhoneOutlined,
  ShopOutlined,
  StarFilled,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks/redux';
import { fetchStoreById } from '../store/slices/storesSlice';
import PageShell from '../components/Layout/PageShell';
const { Paragraph, Text, Title } = Typography;

function statusMeta(status: string | undefined) {
  if (!status) return { color: 'default' as const, label: 'Unknown' };
  const label = status.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const color =
    status === 'approved'
      ? ('success' as const)
      : status === 'pending'
        ? ('warning' as const)
        : status === 'rejected' || status === 'suspended'
          ? ('error' as const)
          : ('processing' as const);
  return { color, label };
}

function formatBusinessType(value: string | undefined): string {
  if (!value) return '—';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function categoryTags(categories: unknown) {
  if (!Array.isArray(categories) || categories.length === 0) {
    return <Text type="secondary">—</Text>;
  }
  return (
    <Space wrap size={[6, 6]} className="max-w-full">
      {categories.map((cat, i) => {
        const label =
          typeof cat === 'string' ? cat : (cat as { name?: string }).name ?? 'Category';
        const key =
          typeof cat === 'string' ? `${cat}-${i}` : ((cat as { _id?: string })._id ?? `cat-${i}`);
        return (
          <Tag key={key} className="m-0">
            {label}
          </Tag>
        );
      })}
    </Space>
  );
}

function websiteHref(url: string): string {
  const t = url.trim();
  if (!t) return '';
  return /^https?:\/\//i.test(t) ? t : `https://${t}`;
}

function publicStorePageUrl(slug: string): string {
  const raw = import.meta.env.VITE_MARKETPLACE_URL || 'http://localhost:3000';
  const base = raw.replace(/\/$/, '');
  return `${base}/stores/${encodeURIComponent(slug)}`;
}

/** Desktop (md+): label | value on one row. Phone: label row, then value row. */
const viewStoreDescLabelRow = { width: 152, fontWeight: 600 as const, verticalAlign: 'top' as const };
const viewStoreDescLabelStacked = { fontWeight: 600 as const };
const viewStoreDescContent = { wordBreak: 'break-word' as const, verticalAlign: 'top' as const };

const ViewStore: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const { currentStore, loading } = useAppSelector((state) => state.stores);
  const routeId = id ?? '';
  const screens = Grid.useBreakpoint();
  /** Below md (~768px): stack label then value; md+: side-by-side. */
  const storeDetailsLayout = screens.md === true ? ('horizontal' as const) : ('vertical' as const);
  const storeDetailsLabelStyle =
    storeDetailsLayout === 'horizontal' ? viewStoreDescLabelRow : viewStoreDescLabelStacked;

  useEffect(() => {
    if (id) dispatch(fetchStoreById(id));
  }, [dispatch, id]);

  const storeMatchesRoute = Boolean(currentStore && routeId && currentStore._id === routeId);
  const showSkeleton = Boolean(
    routeId && loading && (!currentStore || !storeMatchesRoute)
  );
  const showNotFound = Boolean(
    routeId && !loading && (!currentStore || !storeMatchesRoute)
  );

  const visitTooltip =
    currentStore?.verificationStatus === 'approved'
      ? 'Open your public store page in a new tab.'
      : 'Opens your store on the marketplace. Shoppers may not see it until your store is approved.';

  const headerActions = (
    <Space wrap size={8} className="w-full min-[480px]:w-auto">
      <Button
        type="default"
        icon={<ArrowLeftOutlined />}
        className="w-full min-[480px]:w-auto border-slate-200 text-slate-700 hover:border-[#0171BF] hover:!text-[#0171BF]"
        onClick={() => navigate('/stores')}
      >
        My stores
      </Button>
      {storeMatchesRoute && currentStore?.slug ? (
        <Tooltip title={visitTooltip}>
          <Button
            type="default"
            icon={<ExportOutlined />}
            className="w-full min-[480px]:w-auto border-slate-200 text-slate-700 hover:border-[#0171BF] hover:!text-[#0171BF]"
            onClick={() =>
              window.open(publicStorePageUrl(currentStore.slug), '_blank', 'noopener,noreferrer')
            }
          >
            Visit store
          </Button>
        </Tooltip>
      ) : null}
      {storeMatchesRoute ? (
        <Button
          type="primary"
          icon={<EditOutlined />}
          className="w-full min-[480px]:w-auto border-0 bg-gradient-to-r from-[#0171BF] to-[#F7941F] !text-white hover:!text-white shadow-md transition-shadow hover:shadow-lg"
          onClick={() => navigate(`/stores/${routeId}/edit`)}
        >
          Edit
        </Button>
      ) : null}
    </Space>
  );

  const locationLine = useMemo(() => {
    const s = currentStore;
    if (!s?.address) return null;
    const { city, state, country } = s.address;
    const parts = [city, state, country].filter(Boolean);
    if (parts.length === 0) return null;
    return parts.join(', ');
  }, [currentStore]);

  if (!routeId) {
    return (
      <PageShell title="Store details" description="Invalid link — no store id in the URL." titleEnd={headerActions}>
        <Card className="vendor-surface-card max-w-lg rounded-xl border border-slate-200/80 shadow-sm">
          <Button type="primary" size="large" block icon={<ShopOutlined />} onClick={() => navigate('/stores')}>
            Go to My stores
          </Button>
        </Card>
      </PageShell>
    );
  }

  if (showSkeleton) {
    return (
      <PageShell
        title="Store details"
        description="Loading your storefront information."
        titleEnd={headerActions}
      >
        <Card className="vendor-surface-card rounded-xl border border-slate-200/80 shadow-sm">
          <Skeleton active avatar={{ size: 72, shape: 'square' }} paragraph={{ rows: 2 }} />
          <Divider className="my-5" />
          <Skeleton active paragraph={{ rows: 6 }} />
        </Card>
      </PageShell>
    );
  }

  if (showNotFound) {
    return (
      <PageShell
        title="Store not found"
        description="This store may have been removed or the link is incorrect."
        titleEnd={headerActions}
      >
        <Card className="vendor-surface-card max-w-lg rounded-xl border border-slate-200/80 shadow-sm">
          <Space direction="vertical" size="middle" className="w-full">
            <Text type="secondary">Return to your store list and try opening it again.</Text>
            <Button type="primary" size="large" block icon={<ShopOutlined />} onClick={() => navigate('/stores')}>
              Go to My stores
            </Button>
          </Space>
        </Card>
      </PageShell>
    );
  }

  const store = currentStore!;

  const { color: statusColor, label: statusLabel } = statusMeta(store.verificationStatus);

  const totalListings = store.stats?.totalListings ?? 0;
  const activeListings = store.stats?.activeListings ?? 0;
  const totalViews = store.stats?.totalViews ?? 0;
  const rating = store.stats?.rating ?? 0;
  const totalReviews = store.stats?.totalReviews ?? 0;

  return (
    <PageShell
      title={store.storeName}
      description={store.tagline || 'Review how this storefront looks in your account. Scroll for business and address details.'}
      titleEnd={headerActions}
      headerExtra={
        <Tag color={statusColor} className="m-0 px-2.5 py-0.5 text-xs font-medium uppercase tracking-wide">
          {statusLabel}
        </Tag>
      }
    >
      {store.verificationStatus === 'rejected' && store.rejectionReason ? (
        <Alert
          type="error"
          showIcon
          className="mb-4 rounded-xl border-red-200"
          message="Verification was not approved"
          description={store.rejectionReason}
        />
      ) : null}

      {/* Hero — stacks on narrow viewports */}
      <Card className="vendor-surface-card mb-10 overflow-hidden rounded-xl border border-slate-200/80 p-0 shadow-sm sm:mb-12">
        <div className="relative border-b border-slate-100/90 bg-gradient-to-br from-slate-50 via-white to-blue-50/40 px-3 py-5 sm:px-4 sm:py-6">
          <div className="flex flex-col gap-5 sm:flex-row sm:items-center sm:gap-6">
            <div className="flex justify-center sm:justify-start">
              {store.logo ? (
                <Image
                  src={store.logo}
                  alt=""
                  wrapperClassName="!rounded-2xl overflow-hidden ring-2 ring-white shadow-md"
                  className="!rounded-2xl object-cover"
                  width={112}
                  height={112}
                  preview={{ mask: 'Preview' }}
                />
              ) : (
                <Avatar
                  size={112}
                  shape="square"
                  className="!rounded-2xl !bg-gradient-to-br from-[#0171BF]/15 to-[#F7941F]/20 !text-[#0171BF] ring-2 ring-white shadow-md"
                  icon={<ShopOutlined className="text-3xl" />}
                />
              )}
            </div>
            <div className="min-w-0 flex-1 text-center sm:text-left">
              <Title level={3} className="!mb-1 !text-xl !leading-tight !text-slate-900 sm:!text-2xl">
                {store.storeName}
              </Title>
              {store.tagline ? (
                <Text className="block text-sm leading-snug text-slate-600 sm:text-base">{store.tagline}</Text>
              ) : (
                <Text type="secondary" className="block text-sm">
                  No tagline set — add one when you edit the store.
                </Text>
              )}
              {locationLine ? (
                <div className="mt-3 flex items-center justify-center gap-1.5 text-sm text-slate-600 sm:justify-start">
                  <EnvironmentOutlined className="shrink-0 text-slate-400" aria-hidden />
                  <span className="truncate">{locationLine}</span>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        {/* Quick stats — 2×2 on phone, single row from sm if space */}
        <div className="bg-white px-3 py-4 sm:px-3 sm:py-5">
          <div className="grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-4">
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5 text-center sm:text-left">
              <Statistic title="Listings" value={totalListings} valueStyle={{ fontSize: 20, fontWeight: 600 }} />
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5 text-center sm:text-left">
              <Statistic title="Active" value={activeListings} valueStyle={{ fontSize: 20, fontWeight: 600 }} />
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5 text-center sm:text-left">
              <Statistic title="Views" value={totalViews} valueStyle={{ fontSize: 20, fontWeight: 600 }} />
            </div>
            <div className="rounded-xl border border-slate-100 bg-slate-50/60 px-3 py-2.5 text-center sm:text-left">
              <div className="text-[0.875rem] leading-[1.25rem] text-black/45">Rating</div>
              <div className="mt-0.5 flex items-center justify-center gap-1 sm:justify-start">
                <StarFilled className="text-amber-400" />
                <span className="text-lg font-semibold text-slate-900">{Number(rating).toFixed(1)}</span>
                <Text type="secondary" className="text-sm">
                  ({totalReviews} reviews)
                </Text>
              </div>
            </div>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 gap-y-8 lg:grid-cols-12 gap-4">
        <div className="flex min-w-0 flex-col lg:col-span-8 mt-6 gap-4">
          <Card
            title={<span className="text-base font-semibold text-slate-900">Overview</span>}
            className="vendor-surface-card rounded-xl border border-slate-200/80 shadow-sm"
          >
            <Descriptions
              layout={storeDetailsLayout}
              size="middle"
              bordered
              column={1}
              labelStyle={storeDetailsLabelStyle}
              contentStyle={viewStoreDescContent}
              className="view-store-descriptions [&_.ant-descriptions-item-label]:bg-slate-50/80"
            >
              <Descriptions.Item label="Store name">{store.storeName}</Descriptions.Item>
              <Descriptions.Item label="Slug">
                <Text code className="break-all text-sm leading-relaxed">
                  {store.slug || '—'}
                </Text>
              </Descriptions.Item>
              <Descriptions.Item label="Categories">{categoryTags(store.categories as unknown)}</Descriptions.Item>
              <Descriptions.Item label="Description">
                <Paragraph className="!mb-0 whitespace-pre-wrap text-slate-700">
                  {store.description?.trim() ? store.description : '—'}
                </Paragraph>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card
            title={<span className="text-base font-semibold text-slate-900">Business</span>}
            className="vendor-surface-card rounded-xl border border-slate-200/80 shadow-sm"
          >
            <Descriptions
              layout={storeDetailsLayout}
              size="middle"
              bordered
              column={1}
              labelStyle={storeDetailsLabelStyle}
              contentStyle={viewStoreDescContent}
              className="view-store-descriptions [&_.ant-descriptions-item-label]:bg-slate-50/80"
            >
              <Descriptions.Item label="Type">{formatBusinessType(store.businessInfo?.businessType)}</Descriptions.Item>
              <Descriptions.Item label="Since">{store.businessInfo?.yearEstablished ?? '—'}</Descriptions.Item>
              <Descriptions.Item label="Phone">
                {store.businessInfo?.phone ? (
                  <a href={`tel:${store.businessInfo.phone}`} className="inline-flex items-center gap-1.5 text-[#0171BF]">
                    <PhoneOutlined />
                    {store.businessInfo.phone}
                  </a>
                ) : (
                  <Text type="secondary">—</Text>
                )}
              </Descriptions.Item>
              {store.businessInfo?.alternatePhone ? (
                <Descriptions.Item label="Alt. phone">
                  <a href={`tel:${store.businessInfo.alternatePhone}`} className="inline-flex items-center gap-1.5 text-[#0171BF]">
                    <PhoneOutlined />
                    {store.businessInfo.alternatePhone}
                  </a>
                </Descriptions.Item>
              ) : null}
              <Descriptions.Item label="Email">
                {store.businessInfo?.email ? (
                  <a href={`mailto:${store.businessInfo.email}`} className="inline-flex items-center gap-1.5 break-all text-[#0171BF]">
                    <MailOutlined />
                    {store.businessInfo.email}
                  </a>
                ) : (
                  <Text type="secondary">—</Text>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Website">
                {store.businessInfo?.website ? (
                  <a
                    href={websiteHref(store.businessInfo.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-start gap-1.5 break-all text-[#0171BF]"
                  >
                    <GlobalOutlined className="mt-0.5 shrink-0" />
                    {store.businessInfo.website}
                  </a>
                ) : (
                  <Text type="secondary">—</Text>
                )}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card
            title={<span className="text-base font-semibold text-slate-900">Address</span>}
            className="vendor-surface-card rounded-xl border border-slate-200/80 shadow-sm"
          >
            <Descriptions
              layout={storeDetailsLayout}
              size="middle"
              bordered
              column={1}
              labelStyle={storeDetailsLabelStyle}
              contentStyle={viewStoreDescContent}
              className="view-store-descriptions [&_.ant-descriptions-item-label]:bg-slate-50/80"
            >
              <Descriptions.Item label="Street">
                {store.address?.street?.trim() ? store.address.street : '—'}
              </Descriptions.Item>
              <Descriptions.Item label="City">{store.address?.city || '—'}</Descriptions.Item>
              <Descriptions.Item label="State">{store.address?.state || '—'}</Descriptions.Item>
              <Descriptions.Item label="ZIP">{store.address?.zipCode || '—'}</Descriptions.Item>
              <Descriptions.Item label="Country">{store.address?.country || '—'}</Descriptions.Item>
              {store.address?.landmark ? (
                <Descriptions.Item label="Landmark">{store.address.landmark}</Descriptions.Item>
              ) : null}
            </Descriptions>
          </Card>
        </div>

        <div className="min-w-0 lg:col-span-4">
          <div
            className="flex flex-col mt-6 lg:sticky lg:top-[calc(4rem+0.5rem)] lg:max-h-[calc(100vh-5.5rem)] lg:overflow-y-auto lg:pb-2 [&::-webkit-scrollbar]:w-1.5"
          >
            <Card
              title={<span className="text-base font-semibold text-slate-900">Store settings</span>}
              className="vendor-surface-card rounded-xl border border-slate-200/80 shadow-sm"
            >
              <Descriptions
                layout={storeDetailsLayout}
                size="middle"
                bordered
                column={1}
                labelStyle={storeDetailsLabelStyle}
                contentStyle={viewStoreDescContent}
                className="view-store-descriptions [&_.ant-descriptions-item-label]:bg-slate-50/80"
              >
                <Descriptions.Item label="Store active">
                  <Tag color={store.settings?.isActive ? 'success' : 'error'} className="m-0">
                    {store.settings?.isActive ? 'Yes' : 'No'}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Service radius">
                  {store.settings?.serviceRadius != null ? `${store.settings.serviceRadius} km` : '—'}
                </Descriptions.Item>
                <Descriptions.Item label="Auto-reply to leads">
                  <Tag color={store.settings?.autoRespondToLeads ? 'success' : 'default'} className="m-0">
                    {store.settings?.autoRespondToLeads ? 'On' : 'Off'}
                  </Tag>
                </Descriptions.Item>
                {store.settings?.autoResponseMessage ? (
                  <Descriptions.Item label="Auto-reply message">
                    <Paragraph className="!mb-0 whitespace-pre-wrap text-sm text-slate-600">
                      {store.settings.autoResponseMessage}
                    </Paragraph>
                  </Descriptions.Item>
                ) : null}
              </Descriptions>
            </Card>

            {store.banner ? (
              <Card
                title={<span className="text-base font-semibold text-slate-900">Banner</span>}
                className="vendor-surface-card rounded-xl border border-slate-200/80 shadow-sm"
              >
                <Image src={store.banner} alt="" className="w-full rounded-lg" />
              </Card>
            ) : null}
          </div>
        </div>
      </div>
    </PageShell>
  );
};

export default ViewStore;
